import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BookMarked, Play, Loader2, CheckCircle2, AlertTriangle, Calendar, Clock, Info,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

const DAYS = [
  { v: "mon", l: "Lunes" }, { v: "tue", l: "Martes" }, { v: "wed", l: "Miércoles" },
  { v: "thu", l: "Jueves" }, { v: "fri", l: "Viernes" }, { v: "sat", l: "Sábado" },
  { v: "sun", l: "Domingo" },
];

const STATUS_COLOR = {
  running: "bg-blue-100 text-blue-800 border-blue-300",
  done: "bg-emerald-100 text-emerald-800 border-emerald-300",
  done_with_errors: "bg-amber-100 text-amber-800 border-amber-300",
  error: "bg-rose-100 text-rose-800 border-rose-300",
  aborted: "bg-slate-100 text-slate-700 border-slate-300",
};

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDuration(a, b) {
  if (!a || !b) return "—";
  const s = Math.max(0, Math.floor((new Date(b) - new Date(a)) / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

export default function TravefySyncPage() {
  const qc = useQueryClient();
  const [tempMinRatio, setTempMinRatio] = useState(null);

  const statusQ = useQuery({
    queryKey: ["travefy-sync-status"],
    queryFn: async () => (await api.get("/travefy-sync/status")).data,
    refetchInterval: (data) => (data?.active_run?.status === "running" ? 5000 : 30000),
  });
  const runsQ = useQuery({
    queryKey: ["travefy-sync-runs"],
    queryFn: async () => (await api.get("/travefy-sync/runs?limit=15")).data,
    refetchInterval: 30000,
  });
  const cfgMut = useMutation({
    mutationFn: async (patch) => (await api.post("/travefy-sync/config", patch)).data,
    onSuccess: () => {
      toast.success("Configuración guardada");
      qc.invalidateQueries({ queryKey: ["travefy-sync-status"] });
      setTempMinRatio(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const runNowMut = useMutation({
    mutationFn: async () => (await api.post("/travefy-sync/run-now")).data,
    onSuccess: (data) => {
      if (data.queued) {
        toast.success("Sync manual encolado. Login + harvest + consolidate (~2-3 min).");
        qc.invalidateQueries({ queryKey: ["travefy-sync-status"] });
      } else toast.warning(data.reason);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (statusQ.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-72" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  const cfg = statusQ.data?.config;
  const activeRun = statusQ.data?.active_run;
  const lastRun = statusQ.data?.last_run;
  const runs = runsQ.data?.runs || [];

  return (
    <div className="space-y-5" data-testid="travefy-sync-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <BookMarked className="h-6 w-6 text-primary" />
          Sync semanal Travefy → SOFI 2.0
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Cada semana recorre la biblioteca de Travefy (~10.500 items) y consolida SOFI:
          rellena plantillas vacías, mejora las que Travefy tenga más completas y respeta
          las que ya son mejores en SOFI. <strong>Nunca sobreescribe trabajo editorial mejor.</strong>
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card data-testid="travefy-sync-card-config">
          <CardHeader className="pb-2"><CardTitle className="text-base">Configuración</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="tvfy-enabled" className="text-sm">Sync automático activo</Label>
              <Switch
                id="tvfy-enabled"
                checked={!!cfg?.enabled}
                onCheckedChange={(v) => cfgMut.mutate({ enabled: v })}
                data-testid="travefy-sync-enabled-switch"
                disabled={cfgMut.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Día semanal</Label>
              <Select
                value={cfg?.day_of_week || "sun"}
                onValueChange={(v) => cfgMut.mutate({ day_of_week: v })}
              >
                <SelectTrigger className="w-full" data-testid="travefy-sync-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => (
                    <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tvfy-hour" className="text-sm">Hora (UTC)</Label>
              <div className="flex gap-2">
                <Input
                  id="tvfy-hour" type="number" min={0} max={23}
                  value={cfg?.hour_utc ?? 4}
                  onChange={(e) => cfgMut.mutate({ hour_utc: Number(e.target.value) })}
                  className="w-24"
                  data-testid="travefy-sync-hour-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tvfy-ratio" className="text-sm">
                Ratio mínimo Travefy&gt;SOFI (default 1.3 = 30% más)
              </Label>
              <div className="flex gap-2">
                <Input
                  id="tvfy-ratio" type="number" min={1} max={5} step={0.1}
                  value={tempMinRatio ?? cfg?.min_ratio ?? 1.3}
                  onChange={(e) => setTempMinRatio(Number(e.target.value))}
                  className="w-24"
                  data-testid="travefy-sync-ratio-input"
                />
                {tempMinRatio != null && tempMinRatio !== cfg?.min_ratio && (
                  <Button size="sm" onClick={() => cfgMut.mutate({ min_ratio: tempMinRatio })}>
                    Guardar
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-md bg-muted/50 p-2 text-xs">
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                <span className="font-medium">Próxima ejecución:</span>
                <span className="text-muted-foreground">{fmtDT(cfg?.next_run_at) || "no programada"}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" />
                <span className="font-medium">Última ejecución:</span>
                <span className="text-muted-foreground">{fmtDT(cfg?.last_run_at)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2" data-testid="travefy-sync-card-active">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Estado actual</span>
              <Button
                size="sm"
                onClick={() => runNowMut.mutate()}
                disabled={runNowMut.isPending || !!activeRun}
                data-testid="travefy-sync-run-now-btn"
              >
                {runNowMut.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-1.5 h-4 w-4" />
                )}
                {activeRun ? "Sync en curso…" : "Ejecutar sync ahora"}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeRun ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span>Ejecutando pasos: login → harvest → consolidate</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Pasos completados: {(activeRun.steps || []).map((s) => s.name).join(" · ") || "iniciando"}
                </div>
              </div>
            ) : lastRun ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {lastRun.status === "done" ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  )}
                  <div>
                    <div className="font-medium">Último sync: {lastRun.status}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDT(lastRun.started_at)} · {fmtDuration(lastRun.started_at, lastRun.finished_at)}
                    </div>
                  </div>
                </div>
                {lastRun.error && (
                  <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                    {lastRun.error}
                  </div>
                )}
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold">{lastRun.harvested_items || 0}</div>
                    <div className="text-xs text-muted-foreground">Items cosechados</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-emerald-700">{lastRun.updated_count || 0}</div>
                    <div className="text-xs text-muted-foreground">Actualizadas</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-blue-700">{lastRun.sofi_wins_count || 0}</div>
                    <div className="text-xs text-muted-foreground">SOFI ganó</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-lg font-bold text-slate-600">{lastRun.unmatched_count || 0}</div>
                    <div className="text-xs text-muted-foreground">Sin match</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                <Info className="mx-auto mb-2 h-5 w-5" />
                Aún no se ha ejecutado. Púlsalo cuando quieras o activa el sync automático.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="travefy-sync-card-history">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Historial</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {runs.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Sin ejecuciones.</div>
          ) : (
            <div className="divide-y">
              {runs.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/50"
                  data-testid={`travefy-sync-run-row-${r.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline" className={STATUS_COLOR[r.status] || ""}>
                        {r.status}
                      </Badge>
                      <span className="text-muted-foreground">{fmtDT(r.started_at)}</span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">
                        {r.triggered_by === "cron" ? "programado" : "manual"}
                      </span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">
                        {fmtDuration(r.started_at, r.finished_at)}
                      </span>
                    </div>
                    <div className="mt-1 flex gap-4 text-xs text-muted-foreground">
                      {(r.harvested_items ?? 0) > 0 && <span>Items: {r.harvested_items}</span>}
                      <span className="text-emerald-700">Actualizadas: {r.updated_count || 0}</span>
                      <span className="text-blue-700">SOFI ganó: {r.sofi_wins_count || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="rounded-md border-l-4 border-emerald-500 bg-emerald-50 p-3 text-sm text-emerald-900">
        <div className="flex gap-2">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Política respetuosa.</strong> Travefy solo gana si su descripción supera
            en <strong>{cfg?.min_ratio || 1.3}×</strong> a la de SOFI (por defecto: 30% más contenido).
            Si SOFI ya es igual o mejor, NO se toca. Además, imágenes de Travefy se añaden solo
            cuando SOFI no tiene ninguna.
          </div>
        </div>
      </div>
    </div>
  );
}
